import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";

import type { ExtendedProfileT } from "../../../types/database/profiles";

export async function selectNameById(
  id: string,
): Promise<ExtendedProfileT | null> {
  if (!id) {
    throw new Error("No user id provided");
  }

  if (!validateUUID(id)) {
    throw new Error("Invalid user id format");
  }

  const { data, error } = await supabaseClient
    .from("profiles")
    .select("name")
    .eq("user_id", id)
    .returns<ExtendedProfileT>()
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      throw new Error(`Profile not found for user ${id}`);
    }
    throw error;
  }

  if (!data) {
    return null;
  }

  return data;
}

export const createProfileQueryKey = (id: string) => [
  "profile",
  { user_id: id },
];

export const selectNameByIdQueryOptions = (userId: string) => {
  return queryOptions({
    queryKey: createProfileQueryKey(userId),
    queryFn: () => selectNameById(userId),
  });
};

export function useSelectNameById(userId: string) {
  const nameQuery = useSuspenseQuery(selectNameByIdQueryOptions(userId));
  return nameQuery;
}
